<!--
  The announcements the root admin sent (הכרזות, /root-admin/announcements), shown to a business user
  as popups. Mounted once in App.vue for logged-in business users (not the root admin, not the
  reception-desk kiosk, not a frozen account). A few seconds after the app loads it asks
  announcements/pending - the server already decided what is meant for this account and this user
  and what the user is done with - keeps the ones for this kind of screen, and shows them one after
  the other. It asks again when the socket says an announcement was just sent, and every 20 minutes
  for a tab that stays open.

  Every showing is reported (announcements/:id/track): seen once it has been on the screen for a
  moment (SEEN_AFTER_MS - a popup that flashes and goes, closed by a click that was already on its
  way, is not a view and comes back next time), dismiss / click on the footer's buttons (or the ones
  inside the HTML), close on X / Escape / a click outside - closing leaves the rest for the next load.
  It never jumps in while the user is clicking or typing: it waits for a short pause first.

  The root admin inside an account ("כניסה לחשבון") gets the account's live announcements as an admin
  view (adminPreview from the server): each shows once per tab, marked as such, and nothing is
  tracked - looking never uses up the owner's announcement.
-->
<template>

    <AnnouncementCard v-if="current"
                      :key="current.id"
                      :announcement="current"
                      :index="index"
                      :total="queue.length"
                      :leaving="leaving"
                      :adminPreview="adminPreview"
                      @dismiss="onDismiss"
                      @cta="onCta"
                      @close="onClose"
                      @action="onAction"
                      @navigate="onNavigate"
                      @link="onLink" />

</template>

<script>

import axiosV2 from '@/core/http/axios_api_v2';
import AnnouncementCard from './AnnouncementCard.vue';
import { isHostingStationUser } from '@/core/acl/venue';

const RECHECK_MS = 20 * 60 * 1000;
const FIRST_CHECK_MS = 2500;
const QUIET_RETRY_MS = 4000;
const QUIET_MAX_TRIES = 30;
// the user just clicked / typed: wait for this much stillness before the popup comes in
const IDLE_MS = 1500;
const IDLE_RETRY_MS = 1200;
// on the screen this long = seen
const SEEN_AFTER_MS = 1500;
// the admin view: what this tab already showed (nothing is recorded on the server for it)
const PREVIEWED_KEY = 'planupAnnouncementsPreviewed';

function previewedIds() {
    try {
        let ids = JSON.parse(sessionStorage.getItem(PREVIEWED_KEY) || '[]');
        return Array.isArray(ids) ? ids : [];
    } catch (e) {
        return [];
    }
}

function rememberPreviewed(id) {
    try {
        let ids = previewedIds();
        if (ids.indexOf(id) < 0)
            sessionStorage.setItem(PREVIEWED_KEY, JSON.stringify(ids.concat([id])));
    } catch (e) { }
}

// what the popup must not cover: an open modal, an alert, the "add to home screen" popup, the
// frozen-account cover
function screenIsBusy() {

    if (document.querySelector('.modal.show'))
        return true;

    if (document.querySelector('.swal2-container.swal2-shown'))
        return true;

    if (document.querySelector('.homeScreenPrompt'))
        return true;

    // a presentation the customer is looking at (מצב תצוגה ללקוח)
    if (document.querySelector('.customerPresentation, .inventoryPresentation'))
        return true;

    return !!document.querySelector('.accountFrozenModal');

}

function isPhone() {
    return window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
}

export default {

    name: 'AnnouncementPopup',

    components: { AnnouncementCard },

    data() {
        return {
            queue: [],
            index: 0,
            current: null,
            leaving: false,
            checking: false,
            loadedAt: Date.now(),
            timers: [],
            adminPreview: false,
            lastInputAt: 0,
            seenTimer: null,
            seenSent: false,
        }
    },

    mounted() {

        if (!this.eligible())
            return;

        this.later(this.check, FIRST_CHECK_MS);

        this.$root.$on('announcementsChanged', this.onChanged);

        this.recheckTimer = setInterval(this.check, RECHECK_MS);

        //the last click / key anywhere in the app - the popup waits for a pause
        document.addEventListener('pointerdown', this.onUserInput, { capture: true, passive: true });
        document.addEventListener('keydown', this.onUserInput, { capture: true, passive: true });

    },

    beforeDestroy() {

        this.$root.$off('announcementsChanged', this.onChanged);

        clearInterval(this.recheckTimer);

        this.timers.forEach(timer => clearTimeout(timer));
        clearTimeout(this.seenTimer);

        document.removeEventListener('pointerdown', this.onUserInput, { capture: true });
        document.removeEventListener('keydown', this.onUserInput, { capture: true });

    },

    methods: {

        eligible() {

            let user = this.$store.state.user;

            if (!user || user.type == 1 || isHostingStationUser(user))
                return false;

            return !this.$store.state.accountFrozen;

        },

        later(fn, ms) {
            this.timers.push(setTimeout(fn, ms));
        },

        onUserInput() {
            this.lastInputAt = Date.now();
        },

        check() {

            if (this.current || this.checking || !this.eligible())
                return;

            this.checking = true;

            axiosV2('announcements/pending').then(res => {

                let data = (res && res.data) || {};
                let adminPreview = !!data.adminPreview;
                let previewed = adminPreview ? previewedIds() : [];

                let list = (data.announcements || []).filter(announcement => {
                    let devices = (announcement.display && announcement.display.devices) || 'all';
                    if (previewed.indexOf(announcement.id) > -1)
                        return false;
                    return devices == 'all' || (devices == 'mobile') == isPhone();
                });

                if (!list.length || this.current)
                    return;

                this.queue = list;
                this.index = 0;
                this.adminPreview = adminPreview;

                //the first one's delay counts from the app load, not from this answer
                let delay = Math.max(0, (+list[0].display.delaySeconds || 0) * 1000 - (Date.now() - this.loadedAt));

                this.later(() => this.showWhenQuiet(0), delay);

            }).catch(() => { }).finally(() => { this.checking = false; });

        },

        showWhenQuiet(tries) {

            if (this.current || !this.queue.length)
                return;

            if (tries < QUIET_MAX_TRIES) {

                if (screenIsBusy()) {
                    this.later(() => this.showWhenQuiet(tries + 1), QUIET_RETRY_MS);
                    return;
                }

                //a click that is already on its way must not land on the popup
                if (Date.now() - this.lastInputAt < IDLE_MS) {
                    this.later(() => this.showWhenQuiet(tries + 1), IDLE_RETRY_MS);
                    return;
                }

            }

            this.show(0);

        },

        show(index) {

            this.index = index;
            this.leaving = false;
            this.current = this.queue[index];
            this.seenSent = false;

            if (this.adminPreview) {
                rememberPreviewed(this.current.id);
                return;
            }

            let shown = this.current;

            clearTimeout(this.seenTimer);

            this.seenTimer = setTimeout(() => {
                if (this.current !== shown || this.leaving)
                    return;
                this.seenSent = true;
                this.track(shown, 'seen');
            }, SEEN_AFTER_MS);

        },

        track(announcement, action) {

            //the root admin's look is never recorded (the server ignores it anyway)
            if (!announcement || this.adminPreview)
                return;

            //quiet on purpose: a lost report must never put an error in front of the user
            axiosV2.post('announcements/' + announcement.id + '/track', { action: action }).catch(() => { });

        },

        //the card fades out, then the next one comes in (or nothing)
        leave(then) {

            clearTimeout(this.seenTimer);

            this.leaving = true;

            this.later(() => {
                this.leaving = false;
                this.current = null;
                if (then)
                    then();
            }, 220);

        },

        finish() {

            this.leave(() => {
                this.queue = [];
                this.index = 0;
            });

        },

        onDismiss() {

            this.track(this.current, 'dismiss');

            let next = this.index + 1;

            if (next < this.queue.length)
                this.leave(() => this.later(() => this.show(next), 120));
            else
                this.finish();

        },

        //X / Escape / a click outside. Gone before it counted as seen = not shown at all.
        onClose() {

            if (this.seenSent)
                this.track(this.current, 'close');

            this.finish();

        },

        onCta() {

            let display = this.current.display || {};

            this.track(this.current, 'click');

            this.finish();

            this.follow(display.ctaType, display.ctaValue);

        },

        //a button inside the HTML
        onAction(name) {

            if (name == 'close')
                return this.onDismiss();

            if (name == 'cta')
                return this.onCta();

            if (name == 'ai' || name == 'support') {
                this.track(this.current, 'click');
                this.finish();
                this.follow(name);
            }

        },

        onNavigate(path) {

            this.track(this.current, 'click');

            this.finish();

            this.follow('route', path);

        },

        //the link opens in its own tab; the popup has done its job
        onLink() {

            this.track(this.current, 'click');

            this.finish();

        },

        follow(type, value) {

            switch (type) {

                case 'route':
                    if (value && this.$route.fullPath != value)
                        this.$router.push(value).catch(() => { });
                    break;

                case 'url':
                    if (value)
                        window.open(value, '_blank', 'noopener');
                    break;

                case 'ai':
                    this.$root.$emit('openAiInsights');
                    break;

                case 'support':
                    this.$root.$emit('openSupportModal');
                    break;

            }

        },

        //the socket: an announcement was just sent to this account. Every open tab of every user
        //asks at once otherwise - spread them over a few seconds.
        onChanged() {

            if (this.current)
                return;

            this.later(this.check, Math.floor(Math.random() * 6000));

        },

    },

}

</script>
